import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { mapSupabaseError, getUserFriendlyMessage } from '../lib/errors';
import { captureError } from '../lib/sentry';
import { useToast } from '../components/Toast';

/**
 * აქაუნთების მართვის hooks - სრული error handling-ით
 */

// ყველა აქაუნთის მიღება
export function useAccounts() {
  const toast = useToast();
  
  return useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, { 
          tags: { feature: 'accounts', action: 'fetch' }
        });
        throw apiError;
      }
      return data;
    },
    staleTime: 1000 * 60 * 5, // 5 წუთი
    gcTime: 1000 * 60 * 30, // 30 წუთი
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });
}

// აქაუნთების თვიური ლიმიტები
export function useAccountLimits() {
  const toast = useToast();
  
  return useQuery({
    queryKey: ['account-limits'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_monthly_limits')
        .select('*');

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, { 
          tags: { feature: 'accounts', action: 'fetch_limits' }
        });
        throw apiError;
      }
      return data;
    },
    staleTime: 1000 * 60 * 2, // 2 წუთი
    gcTime: 1000 * 60 * 10, // 10 წუთი
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });
}

// ახალი აქაუნთის დამატება
export function useAddAccount() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (accountData) => {
      const { data, error } = await supabase
        .from('accounts')
        .insert([accountData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onError: (error, accountData) => {
      const apiError = mapSupabaseError(error);
      captureError(apiError, { 
        tags: { feature: 'accounts', action: 'create' },
        extra: { accountData }
      });
      toast.error(getUserFriendlyMessage(apiError));
    },
    onSuccess: (newAccount) => {
      toast.success(`აქაუნთი "${newAccount.name}" წარმატებით დაემატა!`);
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['account-limits'] });
    },
  });
}

// აქაუნთის განახლება
export function useUpdateAccount() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const { data: result, error } = await supabase
        .from('accounts')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onError: (error, { id, data }) => {
      const apiError = mapSupabaseError(error);
      captureError(apiError, { 
        tags: { feature: 'accounts', action: 'update' },
        extra: { accountId: id, updatedData: data }
      });
      toast.error(getUserFriendlyMessage(apiError));
    },
    onSuccess: (updatedAccount) => {
      toast.success(`აქაუნთი "${updatedAccount.name}" განახლდა!`);
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['account-limits'] });
    },
  });
}

// აქაუნთის წაშლა (soft delete) - Optimistic Updates-ით
export function useDeleteAccount() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.rpc('delete_account', {
        p_id: id,
        p_reason: 'მომხმარებლის მიერ წაშლა'
      });

      if (error) throw error;
      return id;
    },
    // Optimistic update
    onMutate: async (deletedId) => {
      await queryClient.cancelQueries({ queryKey: ['accounts'] });
      
      const previousAccounts = queryClient.getQueryData(['accounts']);
      
      queryClient.setQueryData(['accounts'], (old) => {
        if (!old) return old;
        return old.filter(account => account.id !== deletedId);
      });
      
      return { previousAccounts };
    },
    onError: (error, id, context) => {
      if (context?.previousAccounts) {
        queryClient.setQueryData(['accounts'], context.previousAccounts);
      }
      
      const apiError = mapSupabaseError(error);
      captureError(apiError, { 
        tags: { feature: 'accounts', action: 'delete' },
        extra: { accountId: id }
      });
      toast.error(getUserFriendlyMessage(apiError));
    },
    onSuccess: (deletedId) => {
      toast.success('აქაუნთი წაიშალა!');
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['account-limits'] });
    },
  });
}

// აქაუნთის თვიური გამოყენება
export function useAccountMonthlyUsage(accountId) {
  const toast = useToast();
  
  return useQuery({
    queryKey: ['account-monthly-usage', accountId],
    queryFn: async () => {
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('items')
        .select('purchase_price_gel')
        .eq('account_id', accountId)
        .gte('order_date', firstDayOfMonth);

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, { 
          tags: { feature: 'accounts', action: 'fetch_monthly_usage' },
          extra: { accountId }
        });
        throw apiError;
      }

      const total = data.reduce((sum, item) => sum + (item.purchase_price_gel || 0), 0);
      return total;
    },
    enabled: !!accountId,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });
}
